import type { ChangeEvent } from 'react'
import type { SingleValue } from 'react-select'

import { keepPreviousData } from '@tanstack/react-query'
import cx from 'classnames'
import _ from '../../../../utils/lodash-optimized'
import { useState } from 'react'
import {
  MdAutorenew,
  MdDone, MdOutlineFolderShared
} from 'react-icons/md'
import { RiFolderTransferLine } from 'react-icons/ri'
import { Link, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import { useDebounce } from 'react-use'
// @ts-expect-error //it's okay
import statuses from 'shared_src/dictionaries/episodeOfCare/statuses'

import { useAppSelector } from '../../../../store'
import { useAdvancedSearchHook, StatusesEnum } from '../../../advanced-search/advanced-search-hook'
import * as Table from '../../../claim-history/simple-table'
import { NoDataPlaceHolder } from '../../../PatientFolder/behavioral/components'
import VIPBadge from '../../../shared/badge/vip'
import { ContextMenuButton } from '../../../shared/ContextMenu'
import FriendlyDate from '../../../shared/Date/FriendlyDate'
import * as Sidebar from '../components/index'

import styles from './patient-search.module.css'

interface Status { label: string, value: StatusesEnum }

export function PatientSearch (): JSX.Element {
  const navigate = useNavigate()
  let [ searchValue, setSearchValue ] = useState<string | undefined>('')
  let [ debouncedSearchValue, setDebouncedSearchValue ] = useState<string | undefined>('')
  let [ status, setStatus ] = useState<StatusesEnum>(StatusesEnum.all)
  let { clients } = useAppSelector(state => state.globalVars)
  let [ debouncedIsFetching, setDebouncedIsFetching ] = useState(false)
  let _statuses = statuses as Status[]

  const [ query ] = useAdvancedSearchHook({
    anyName: debouncedSearchValue ?? '',
    scope: 'all',
    status,
    count: 15,
    page: 0,
    clientName: 'all',
    sortBy: 'lastName',
    sortDir: 'asc',
    managingOrg: 'all',
    patientId: undefined,
    memberId: undefined,
    phone: undefined,
    ssn: undefined,
    birthDate: undefined,
    opensearch: true
  }, {
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    retry: false
  })

  useDebounce(() => {
    setDebouncedSearchValue(searchValue)
  }, 500, [ searchValue ])

  useDebounce(() => {
    setDebouncedIsFetching(query.isFetching)
  }, 200, [ query.isFetching ])

  function handleChangeInput (e: ChangeEvent<HTMLInputElement>): void {
    setSearchValue(e.target.value)
  }

  function handleStatusChange (v: SingleValue<{ value: StatusesEnum, label: string }>): void {
    setStatus(v?.value ?? StatusesEnum.all)
  }

  let advancedPatientSearchLink = `/patient-search?status=${status ?? 'all'}&anyName=${searchValue ?? ''}`

  return (
    <>
      <Sidebar.Header>
        Patient Search
      </Sidebar.Header>
      <Sidebar.Wrapper>
        <form className='px-4 pt-1 border-t-0 border-l-0 border-r-0 border-b border-solid border-b-gray-200'>
          <div style={{ position: 'relative' }}>
            <input
              className={styles.input}

              autoFocus
              type='text'
              value={searchValue ?? ''}
              onChange={handleChangeInput}
              placeholder='Search by Patient Name'
            />
            <div title={(searchValue?.replace(/ /g, '')?.length ?? 0) < 1 ? 'Please enter at least one character to start searching' : undefined}>
              <MdDone
                className={cx(styles.statusIcon,
                  [ (!debouncedIsFetching && (searchValue?.replace(/ /g, '')?.length ?? 0) >= 1)
                    ? [ styles.visible ]
                    : [ styles.hidden ] ]
                )}
                width={19}
                height={19}
              />
              <MdAutorenew
                className={cx(styles.statusIcon,
                  [ (debouncedIsFetching && (searchValue?.replace(/ /g, '')?.length ?? 0) >= 1)
                    ? [ styles.visible ]
                    : [ styles.hidden ]
                  ]
                )}
                size='1.2rem'
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
            <Select
              styles={{
                control: (styles: any) => ({
                  ...styles,
                  backgroundColor: 'white',
                  border: '1px solid #D0D4DB',
                  borderRadius: '6px',
                  boxShadow: 'none'
                }),
                container: (styles: any) => ({
                  ...styles,
                  flex: 1,
                  backgroundColor: 'white',
                  boxShadow: 'none',
                  borderRadius: '8px'
                })
              }}
              isClearable={false}
              options={_statuses}
              isMulti={false}
              value={{ value: status, label: _statuses.find((s) => s.value === status)?.label ?? 'Unknown' }}
              onChange={handleStatusChange}
            />
          </div>

          <div
            className={styles.advancedSearchContainer}
          >
            <Link
              className={styles.advancedSearch}
              to={advancedPatientSearchLink}
              title='Open Advanced Search page'
            >
              Advanced Search
            </Link>
          </div>
        </form>

        <Table.ContainerMeasureProvider>
          <Sidebar.Cards>
            {query?.data?.data?.map(({ firstName, lastName, id, _clientName, birthDate, status, openEncounterStatus, isVIPPatient }) => (
              <Sidebar.Card key={id}>
                <Link
                  title='Open Patient Folder'
                  style={{
                    flex: 1,
                    overflow: 'hidden'
                  }}
                  to={`/patient/${_clientName}/${id}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <VIPBadge isVIP={isVIPPatient} />
                    <p className={cx(styles.patientName, 'truncate')}>{firstName} {lastName}</p>
                  </div>
                  <p
                    className='truncate'
                    style={{ color: '#9d98a4', lineHeight: '21px' }}
                  >
                    {clients?.find((client) => client.clientName === _clientName)?.display}
                  </p>

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <p style={{ color: '#9d98a4', lineHeight: '21px' }}><FriendlyDate date={birthDate || ''} /></p>
                    <p style={{ color: '#9d98a4', lineHeight: '21px' }}>
                      {status
                        ? _.find(statuses, x => x.value === status).label
                        : 'Unknown'}
                    </p>
                  </div>
                </Link>
                <ContextMenuButton
                  actions={[
                    {
                      id: 'patient-holder',
                      icon: MdOutlineFolderShared,
                      label: 'Patient Folder',
                      onClick: () => {
                        navigate(`/patient/${_clientName}/${id}`)
                      }
                    },
                    ...(openEncounterStatus && [ 'in-progress', 'onleave' ].includes(openEncounterStatus)
                      ? [{
                        id: 'care-plan',
                        icon: RiFolderTransferLine,
                        label: 'Care Plan',
                        onClick: () => {
                          navigate(`/patient/${_clientName}/${id}/assessment/igao`)
                        }
                      }]
                      : [])
                  ]}
                  onClose={() => ({})}
                  direction='bottom-left'
                />
              </Sidebar.Card>
            ))}

            {!query.isFetched && !query?.data?.data?.length && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span className={styles.placeholderText}>Start typing to search patients</span>
              </div>
            )}

            {!query?.data?.data?.length && !query.isLoading && query.isFetched && (
              <>
                <NoDataPlaceHolder
                  className='bg-transparent'
                  title='No results match the search criteria'
                />

                <Link
                  className={cx(styles.newPatientButton, 'bg-primary bg-opacity-10 text-primary transition-colors hover:bg-opacity-[15%]')}
                  to='/new-patient'
                >
                  Add New Patient
                </Link>
              </>
            )}
          </Sidebar.Cards>
        </Table.ContainerMeasureProvider>

      </Sidebar.Wrapper>
    </>
  )
}
